import { ReactNode, useEffect, useRef } from "react";
import { sentinelTokens } from "@/app/providers/DesignSystemContext";
import { useTheme } from "@/app/providers/ThemeContext";
import { handleLandingAnchorClick } from "@/landing/landingScroll";

interface EnterpriseHeroProps {
  badge?: string;
  headline: string;
  headlineAccent?: string;
  subheadline: string;
  primaryCta?: {
    text: string;
    href: string;
  };
  secondaryCta?: {
    text: string;
    href: string;
  };
  children?: ReactNode;
  className?: string;
}

export default function EnterpriseHero({
  badge = "Live Data · Intelligence · Analysis & Alerts",
  headline,
  headlineAccent,
  subheadline,
  primaryCta,
  secondaryCta,
  children,
  className,
}: EnterpriseHeroProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !isDark) {
      grid.style.backgroundImage = "none";
      return;
    }

    grid.style.backgroundImage = `
      linear-gradient(rgba(0, 184, 219, 0.015) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 184, 219, 0.015) 1px, transparent 1px)
    `;
    grid.style.backgroundSize = "40px 40px";
  }, [isDark]);

  const tokens = sentinelTokens;

  return (
    <section
      id="hero"
      className={`relative min-h-screen flex flex-col justify-between overflow-hidden ${className ?? ""}`}
      style={{
        background: isDark ? tokens.colors.bg : "#f7f7f5",
        fontFamily: tokens.fonts.mono,
      }}
    >
      {/* Grid Background */}
      <div
        ref={gridRef}
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 184, 219, 0.015) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 184, 219, 0.015) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
        aria-hidden="true"
      />

      {/* Ambient Glow */}
      <div
        className="fixed pointer-events-none z-0"
        style={{
          top: "-120px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "900px",
          height: "300px",
          background: `radial-gradient(ellipse, ${isDark ? tokens.colors.cyanGlow : "rgba(0, 184, 219, 0.04)"} 0%, transparent 70%)`,
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 px-6 sm:px-10 lg:px-16 pt-32 pb-16">
       <div className="max-w-6xl mx-auto">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 mb-8"
          style={{
            fontSize: "0.65rem",
            letterSpacing: "0.18em",
            color: isDark ? tokens.colors.textDim : "rgba(0,0,0,0.4)",
            textTransform: "uppercase",
          }}
        >
          <div
            className="rounded-full"
            style={{
              width: "6px",
              height: "6px",
              background: tokens.colors.cyan,
              animation: tokens.animations.pulse,
            }}
          />
          {badge}
        </div>

        {/* Headline */}
        <h1
          style={{
            fontFamily: tokens.fonts.display,
            fontSize: "clamp(2.5rem, 8vw, 5rem)",
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            color: isDark ? "#ffffff" : "#111111",
          }}
        >
          {headline}
          {headlineAccent && (
            <>
              <br />
              <span style={{ color: tokens.colors.cyan }}>{headlineAccent}</span>
            </>
          )}
        </h1>

        {/* Subheadline */}
        <p
          className="mt-6 max-w-xl"
          style={{
            fontSize: "0.9rem",
            lineHeight: 1.7,
            color: isDark ? tokens.colors.textDim : "#4b5563",
          }}
        >
          {subheadline}
        </p>

        {/* CTAs */}
        {(primaryCta || secondaryCta) && (
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            {primaryCta && (
              <a
                href={primaryCta.href}
                onClick={(e) => handleLandingAnchorClick(e, primaryCta.href)}
                className="inline-flex items-center justify-center no-underline transition-all duration-150 hover:brightness-110"
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  padding: "12px 24px",
                  background: tokens.colors.cyan,
                  color: "#000000",
                  border: `1px solid ${tokens.colors.cyan}`,
                  borderRadius: "9999px",
                }}
              >
                {primaryCta.text} →
              </a>
            )}
            {secondaryCta && (
              <a
                href={secondaryCta.href}
                onClick={(e) => handleLandingAnchorClick(e, secondaryCta.href)}
                className="inline-flex items-center justify-center no-underline transition-all duration-150"
                style={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.06em",
                  padding: "12px 24px",
                  background: "transparent",
                  color: isDark ? tokens.colors.cyan : "text-cyan-600",
                  border: `1px solid ${isDark ? tokens.colors.borderBright : "#c5bbb0"}`,
                  borderRadius: "9999px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDark ? tokens.colors.cyanGlow : "rgba(0, 184, 219, 0.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {secondaryCta.text}
              </a>
            )}
          </div>
        )}

        {/* Child content (e.g., product demo) */}
        {children && <div className="mt-12">{children}</div>}
       </div>
      </div>
    </section>
  );
}
